import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { getCurrentPlatform, Platform } from '@rbx/core';
import { Dialog, makeStyles } from '@rbx/ui';
import StudioDialog from '../components/StudioDialog';
import { getStudioProtocolURL, openStudioProtocol, waitForStudioCheck } from '../utils/studioUtils';
import { useStudioResources } from '../StudioResourcesContext';
import {
  EStudioTaskType,
  type StudioDialogParams,
  type StudioDialogTranslations,
  type StudioDialogEventHandlers,
  TStudioProtocolScheme,
  TStudioDistributorType,
} from '../types';
import fetchMagicToken from '../utils/fetchMagicToken';

type TUseStudioOutput = {
  isCompatible: boolean;
  open: (
    params: StudioDialogParams,
    context: {
      userId?: number;
      locale: string;
      protocolScheme: TStudioProtocolScheme;
      distributorType: TStudioDistributorType;
      baseUrl?: string;
    },
  ) => Promise<void>;
  dialog: React.ReactElement;
  getStudioDownloadUrlAsync: () => Promise<string>;
};

enum EDialogState {
  Error = 'Error',
  Download = 'Download',
  Loading = 'Loading',
}

const useStudioDialogStyles = makeStyles()((theme) => ({
  dialog: {
    backgroundColor: theme.palette.background.media,
    padding: '40px 20px 30px 20px',
    minWidth: 400,
  },
}));

const useStudio = (
  translations: StudioDialogTranslations,
  eventHandlers?: StudioDialogEventHandlers,
): TUseStudioOutput => {
  const currentPlatform = getCurrentPlatform();

  const resources = useStudioResources();
  const {
    classes: { dialog },
  } = useStudioDialogStyles();

  const [dialogState, setDialogState] = useState<EDialogState>();
  const [magicToken, setMagicToken] = useState<string>();
  const dialogStateRef = useRef(dialogState);
  const paramsRef = useRef<StudioDialogParams | undefined>(undefined);

  async function sha1Hex(token: string): Promise<string> {
    const buffer = await crypto.subtle.digest('SHA-1', new TextEncoder().encode(token));
    const hashArray = Array.from(new Uint8Array(buffer));
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  }

  const getStudioDownloadUrlAsync = useCallback(async () => {
    const token = await fetchMagicToken(resources);
    return typeof token === 'undefined'
      ? resources.downloadURL
      : `${resources.downloadURL}?code=${token}`;
  }, [resources]);

  const getDownloadCode = useCallback(async () => {
    return typeof magicToken === 'undefined' ? undefined : sha1Hex(magicToken);
  }, [magicToken]);

  const onDialogParamsChange = useCallback((params: StudioDialogParams | undefined) => {
    paramsRef.current = params;
  }, []);
  const onDialogStateChange = useCallback((state: typeof dialogState) => {
    dialogStateRef.current = state;

    setDialogState(state);
  }, []);
  const handleWindowBlur = useCallback(() => {
    if (dialogStateRef.current === EDialogState.Download) {
      onDialogParamsChange(undefined);
      onDialogStateChange(undefined);
    }
  }, [onDialogParamsChange, onDialogStateChange]);

  useEffect(() => {
    window.addEventListener('blur', handleWindowBlur);
    return () => {
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, [handleWindowBlur]);

  const open: TUseStudioOutput['open'] = useCallback(
    async (params, { userId, locale, protocolScheme, distributorType, baseUrl }) => {
      try {
        onDialogParamsChange(params);
        onDialogStateChange(EDialogState.Loading);
        eventHandlers?.startAttempt?.(params);

        const [userChannel, token] = await Promise.all([
          resources.fetchUserChannel(),
          fetchMagicToken(resources),
        ]);
        setMagicToken(token);

        const baseStudioProtocolArgs = {
          userId: userId?.toString() ?? '',
          locale,
          protocolScheme,
          distributorType,
          baseUrl,
          channel: userChannel,
        };

        let studioProtocolUrl = '';
        switch (params.task) {
          case EStudioTaskType.EditPlace:
            studioProtocolUrl = getStudioProtocolURL({
              ...baseStudioProtocolArgs,
              task: params.task,
              universeId: params.universeId,
              placeId: params.placeId,
            });
            break;
          case EStudioTaskType.ViewAsset:
            studioProtocolUrl = getStudioProtocolURL({
              ...baseStudioProtocolArgs,
              task: params.task,
              assetId: params.assetId,
            });
            break;
          default:
            studioProtocolUrl = getStudioProtocolURL({
              ...baseStudioProtocolArgs,
              task: params.task,
            });
        }

        openStudioProtocol(studioProtocolUrl);
        eventHandlers?.startSuccess?.(params);

        await waitForStudioCheck();
        onDialogStateChange(EDialogState.Download);
      } catch (e) {
        onDialogStateChange(EDialogState.Error);
        resources.onError(e as Error, params);
      }
    },
    [eventHandlers, onDialogParamsChange, onDialogStateChange, resources],
  );

  const downloadUrl =
    typeof magicToken === 'undefined'
      ? resources.downloadURL
      : `${resources.downloadURL}?code=${magicToken}`;

  const studioLoadingDialog = useMemo(
    () => (
      <Dialog classes={{ paper: dialog }} maxWidth='XSmall' open={dialogState !== undefined}>
        {dialogState === EDialogState.Loading && (
          <StudioDialog.Loading logoSrc={resources.logoSrc} translations={translations} />
        )}
        {dialogState === EDialogState.Error && (
          <StudioDialog.Error
            onClose={() => {
              onDialogParamsChange(undefined);
              onDialogStateChange(undefined);
            }}
            logoSrc={resources.logoSrc}
            translations={translations}
          />
        )}
        {dialogState === EDialogState.Download && (
          <StudioDialog.Download
            onClose={() => {
              onDialogParamsChange(undefined);
              onDialogStateChange(undefined);
            }}
            onDownload={() => {
              if (typeof paramsRef.current !== 'undefined') {
                eventHandlers?.download?.(paramsRef.current, { getDownloadCode });
              }
            }}
            downloadUrl={downloadUrl}
            logoSrc={resources.logoSrc}
            translations={translations}
          />
        )}
      </Dialog>
    ),
    [
      dialog,
      dialogState,
      getDownloadCode,
      downloadUrl,
      eventHandlers,
      onDialogParamsChange,
      onDialogStateChange,
      resources.logoSrc,
      translations,
    ],
  );

  return {
    open,
    dialog: studioLoadingDialog,
    isCompatible: currentPlatform === Platform.Windows || currentPlatform === Platform.macOS,
    getStudioDownloadUrlAsync,
  };
};

export default useStudio;
